import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, QrCode, Star } from "lucide-react";
import { encuestasApi, type EncuestaQrActiva } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { EncuestaQrGrandeModal } from "../components/EncuestaQrGrandeModal";
import { useInicio } from "../lib/permisos";

/**
 * Página mínima de solo-lectura para personal con contacto directo con el
 * cliente (cajero, mesonero/operativo): ver y mostrar el QR de las encuestas
 * activas, sin acceso a crear/editar/enviar/eliminar encuestas.
 */
export default function EncuestasQrPage() {
  const inicio = useInicio();
  const [abierta, setAbierta] = useState<EncuestaQrActiva | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["encuestas-qr-activas"],
    queryFn: () => encuestasApi.listarActivasQr(),
    refetchInterval: 60_000,
  });

  return (
    <div className="min-h-screen bg-ink-50">
      <PageHeader
        crumbs={[{ label: "Inicio", to: inicio }, { label: "Encuestas (QR)" }]}
        icon={Star}
      />

      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6">
        <p className="text-sm text-ink-500 mb-4">
          Tocá una encuesta para mostrarle el QR en grande al cliente y que lo escanee con su celular.
        </p>

        <div className="card">
          {isLoading ? (
            <div className="p-10 text-center text-ink-400">
              <Loader2 className="size-5 animate-spin mx-auto" />
            </div>
          ) : !data || data.length === 0 ? (
            <div className="p-12 text-center text-ink-400">
              <QrCode className="size-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No hay encuestas con QR activo en este momento</p>
            </div>
          ) : (
            <div className="divide-y divide-ink-100">
              {data.map((e) => (
                <button key={e.id} onClick={() => setAbierta(e)}
                  className="w-full px-5 py-4 flex items-center gap-4 hover:bg-ink-50 transition-colors text-left">
                  <div className="size-11 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                    <QrCode className="size-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-ink-800">{e.titulo}</p>
                    {e.sucursalNombre && <p className="text-xs text-ink-500">{e.sucursalNombre}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {abierta && <EncuestaQrGrandeModal encuesta={abierta} onClose={() => setAbierta(null)} />}
    </div>
  );
}
